import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useKiaoConfirm } from '@kiao-voice/ui-kit'
import { isTeamMemberDisabledError } from '@kiao-voice/api-client'
import { reportHandledException, reportMemberUploadFailure } from '@/lib/error-reporting'
import {
  AudioBatchApiError,
  getAudioBatchUploadFailureContext,
  isRecoverableActiveContextError,
  submitAudioBatch,
  submitAudioBatchLinks,
} from './api-client'
import { describeValidationError } from './file-validation'
import { describeLinkValidationError } from './source-links'
import { uploadCancelCopy } from './upload-progress-panel'
import { useUploadSubmitProgress } from './use-upload-submit-progress'
import type {
  AudioBatchPreflightRequest,
  AudioBatchPreflightResponse,
  AudioBatchSubmitResponse,
} from './types'
import type { preflightAudioBatch } from './api-client'

/** Everything the payload builder may add beyond the files themselves. */
export type SubmitExtras = Omit<NonNullable<Parameters<typeof submitAudioBatch>[1]>, 'signal'>

/** One upload's payload and its follow-up, frozen at the moment Start is pressed. */
export interface SubmissionSnapshot {
  options: SubmitExtras
  /** Feed the local frecency store the terms that actually shipped. */
  recordShipped: () => void
}

export interface UploadLifecycleOptions {
  queue: File[]
  /**
   * Link mode: the links to submit instead of `queue`, already validated by the
   * dialog. Null in file mode. Links skip the duration probe and preflight: the
   * length is only known once the worker has the media.
   */
  links: string[] | null
  /**
   * Whether Start is held down — an over-cap hint list, or dictionary terms
   * still in flight. Read at CALL time: both are derived downstream of this
   * hook's own `submitting`.
   */
  isBlocked: () => boolean
  loadStoredToken: () => string | null
  submit: typeof submitAudioBatch
  submitLinks: typeof submitAudioBatchLinks
  preflight: typeof preflightAudioBatch | null
  probeDuration: (file: File) => Promise<number | null>
  /** Per-file duration cap of the current plan, or null when uncapped. */
  maxFileMinutes: number | null
  /**
   * Everything this upload ships with, captured ONCE as Start is pressed —
   * before the duration probe, which can take seconds. A refocus catch-up
   * deliberately does not hold Start down, so re-reading afterwards could send
   * a hint list the 50-cap gate never saw (a whole-batch 422) and record usage
   * for terms that moved group meanwhile.
   */
  captureSubmission: () => SubmissionSnapshot
  /** A queued file is longer than the plan allows — the page opens its dialog. */
  onOverCap: (maxMinutes: number, actualMinutes: number, plan?: string) => void
  onCreditsShort: (
    response: Extract<AudioBatchPreflightResponse, { reason: 'insufficient_credits' }>
  ) => void
  /** 422 → per-file validation messages, rendered inline by the page. */
  onValidationErrors: (messages: string[]) => void
  onSubmitted: (response: AudioBatchSubmitResponse) => void
}

export interface UploadLifecycle {
  submitting: boolean
  /** A whole-submit failure, shown above the queue. Null while nothing failed. */
  submitError: string | null
  setSubmitError: (message: string | null) => void
  /** The progress the panel and footer render. */
  progress: ReturnType<typeof useUploadSubmitProgress>['progress']
  start: () => Promise<void>
  /** The only way out during an upload: confirm, abort, reset. */
  cancel: () => Promise<void>
  /**
   * Open a new session and abort whatever the previous one left in flight. Every
   * async path in the modal re-checks the session it started under, so a request
   * that resolves after a close (or a reopen) can never write into the new one.
   */
  beginSession: () => void
  /** The session number to capture before an async mutation. */
  currentSession: () => number
  isCurrentSession: (session: number) => boolean
  /** Drop the in-flight transfer without confirming (the modal closed). */
  abortInFlight: () => void
  reset: () => void
}

/**
 * Submitting an upload: the Start guard, the pre-upload duration probe, the
 * transfer itself, its progress, and the one confirmed way to cancel it.
 *
 * CONSTRAINT: call this unconditionally, above any early return the modal makes
 * for its closed state. It mounts hooks of its own, so a conditional call
 * changes the hook order between renders.
 */
export function useUploadLifecycle({
  queue,
  links,
  isBlocked,
  loadStoredToken,
  submit,
  submitLinks,
  preflight,
  probeDuration,
  maxFileMinutes,
  captureSubmission,
  onOverCap,
  onCreditsShort,
  onValidationErrors,
  onSubmitted,
}: UploadLifecycleOptions): UploadLifecycle {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const upload = useUploadSubmitProgress()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const submitAbortRef = useRef<AbortController | null>(null)
  // Bumped on every open/close transition so a submit that resolves AFTER the
  // modal was closed (and possibly reopened) is ignored — it must not fire
  // onSubmitted into a stale/new session.
  const sessionRef = useRef(0)

  const abortInFlight = useCallback(() => {
    submitAbortRef.current?.abort()
    submitAbortRef.current = null
  }, [])

  const beginSession = useCallback(() => {
    sessionRef.current += 1
  }, [])

  const currentSession = useCallback(() => sessionRef.current, [])
  const isCurrentSession = useCallback((session: number) => session === sessionRef.current, [])

  const cancel = useCallback(async () => {
    const cancelled = await upload.requestCancel(confirm, uploadCancelCopy(t), abortInFlight)
    if (cancelled) setSubmitting(false)
  }, [abortInFlight, confirm, t, upload])

  const start = useCallback(async () => {
    const itemCount = links ? links.length : queue.length
    if (itemCount === 0 || submitting || isBlocked()) return
    const token = loadStoredToken()
    if (!token) {
      setSubmitError(t('transcriptions.upload.submitFailed'))
      return
    }
    const session = sessionRef.current
    // Frozen here, not after the probe: see `captureSubmission`.
    const submission = captureSubmission()
    setSubmitting(true)
    setSubmitError(null)
    upload.beginPreparing(itemCount)

    const uploadController = new AbortController()
    submitAbortRef.current = uploadController
    const isActive = () => session === sessionRef.current && !uploadController.signal.aborted
    try {
      if (links) {
        const response = await submitLinks(links, {
          signal: uploadController.signal,
          ...submission.options,
          ...upload.submitCallbacks,
        })
        if (!isActive()) return
        submission.recordShipped()
        onSubmitted(response)
        return
      }
      const files: AudioBatchPreflightRequest['files'] = []
      const probe = async (file: File, index: number) => {
        upload.noteFileChecked(index, queue.length)
        const duration = await probeDuration(file).catch((cause: unknown) => {
          if (
            (cause instanceof Error || cause instanceof DOMException) &&
            cause.name === 'AbortError'
          )
            throw cause
          reportHandledException(cause, {
            source: 'features/batch-transcription/use-upload-lifecycle.ts',
          })
          return null
        })
        if (!isActive()) return null
        return duration !== null && Number.isFinite(duration) && duration > 0 ? duration : null
      }
      if (maxFileMinutes != null) {
        for (const [index, file] of queue.entries()) {
          const durationSecs = await probe(file, index)
          if (!isActive()) return
          files.push({ durationSecs })
          if (durationSecs !== null && durationSecs > maxFileMinutes * 60) {
            setSubmitting(false)
            upload.reset()
            onOverCap(maxFileMinutes, durationSecs / 60)
            return
          }
        }
      } else if (preflight !== null) {
        // At most five files: metadata timeouts run together, preserving queue order.
        files.push(
          ...(await Promise.all(
            queue.map(async (file, index) => ({ durationSecs: await probe(file, index) }))
          ))
        )
        if (!isActive()) return
      }
      if (preflight && files.some((file) => file.durationSecs !== null)) {
        try {
          const result = await preflight(
            { files, visualContext: submission.options.visualContext },
            { signal: uploadController.signal }
          )
          if (!isActive()) return
          if (!result.ok) {
            setSubmitting(false)
            upload.reset()
            if (result.reason === 'insufficient_credits') onCreditsShort(result)
            else onOverCap(result.maxFileMinutes, result.durationSecs / 60, result.plan)
            return
          }
        } catch (cause) {
          if (
            !isActive() ||
            ((cause instanceof Error || cause instanceof DOMException) &&
              cause.name === 'AbortError')
          )
            throw cause
          if (cause instanceof AudioBatchApiError && cause.status >= 400 && cause.status < 500)
            throw cause
          reportHandledException(cause, {
            source: 'features/batch-transcription/use-upload-lifecycle.ts',
          })
          console.warn('[upload-modal] preflight unavailable; worker will check allowance')
        }
      }
      if (!isActive()) return
      const response = await submit(queue, {
        signal: uploadController.signal,
        ...submission.options,
        ...upload.submitCallbacks,
      })
      // The modal was closed (and maybe reopened) while this was in flight —
      // don't fire onSubmitted into a stale session.
      if (!isActive()) return
      submission.recordShipped()
      onSubmitted(response)
    } catch (cause) {
      // An abort is the cancel flow's own doing; it already set the cancelled
      // state and must not be overwritten or reported as an error.
      if ((cause instanceof Error || cause instanceof DOMException) && cause.name === 'AbortError')
        return
      const failure = getAudioBatchUploadFailureContext(cause)
      reportMemberUploadFailure({
        cause,
        phase: failure?.phase ?? 'unknown',
        ...(failure?.directFallback ? { directFallback: true } : {}),
        ...(cause instanceof AudioBatchApiError
          ? {
              status: cause.status,
              ...(cause.code ? { code: cause.code } : {}),
            }
          : {}),
      })
      if (!isActive()) return
      setSubmitting(false)
      upload.reset()
      // Map known outcomes to actionable, localized feedback; the modal stays
      // open so the user can fix and retry — never a silent failure.
      if (cause instanceof AudioBatchApiError) {
        // 422 → per-file validation errors inline.
        if (cause.validationErrors?.length) {
          onValidationErrors(
            cause.validationErrors.map((err) =>
              links ? describeLinkValidationError(err, links, t) : describeValidationError(err, t)
            )
          )
          return
        }
        // 402 → out of credits: point at the top-up path (was a CTA on the old page).
        if (cause.code === 'insufficient_credits') {
          setSubmitError(t('transcriptions.upload.insufficientCredits'))
          return
        }
        // A disabled team seat is not a dead session: say so, keep the
        // workspace and the sign-in.
        if (isTeamMemberDisabledError(cause)) {
          setSubmitError(t('team.errMemberDisabled'))
          return
        }
        // A genuine 401/403 (not a self-healing stale context) → the session is
        // dead; tell the user to re-auth rather than retrying into a wall.
        if (
          (cause.status === 401 || cause.status === 403) &&
          !isRecoverableActiveContextError(cause)
        ) {
          setSubmitError(t('transcriptions.upload.sessionExpired'))
          return
        }
      }
      console.error('[upload-modal] submit failed', cause)
      setSubmitError(t('transcriptions.upload.submitFailed'))
    } finally {
      if (submitAbortRef.current === uploadController) submitAbortRef.current = null
    }
  }, [
    isBlocked,
    captureSubmission,
    links,
    loadStoredToken,
    maxFileMinutes,
    onOverCap,
    onCreditsShort,
    onSubmitted,
    onValidationErrors,
    preflight,
    probeDuration,
    queue,
    submit,
    submitLinks,
    submitting,
    t,
    upload,
  ])

  const reset = useCallback(() => {
    setSubmitting(false)
    setSubmitError(null)
  }, [])

  return {
    submitting,
    submitError,
    setSubmitError,
    progress: upload.progress,
    start,
    cancel,
    beginSession,
    currentSession,
    isCurrentSession,
    abortInFlight,
    reset,
  }
}
